import {Location} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, User} from 'firebase/auth';
import {doc, getDoc, getFirestore, updateDoc} from 'firebase/firestore';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {Router} from '@angular/router';
import {MenuController} from '@ionic/angular/standalone';
import {ErrorHandlerService, StorageService, TranslateHelperService} from '../shared';

/**
 * Service d'authentification gérant la connexion, déconnexion et vérification d'email.
 *
 * Ce service fournit :
 * - La connexion avec email/mot de passe
 * - La déconnexion
 * - L'envoi d'email de vérification
 * - La réinitialisation de mot de passe
 * - La vérification de l'état de connexion
 *
 * @example
 * ```typescript
 * // Connexion
 * await this.authService.login(email, password, '/app/home');
 *
 * // Vérifier si connecté
 * if (this.authService.isLoggedIn) {
 *   // Utilisateur connecté
 * }
 * ```
 */
@Injectable()
export class AuthenticationService {
	private readonly firebaseInit = inject(FirebaseInitService);

	private get firestore() {
		return getFirestore(this.firebaseInit.app);
	}

	private get auth() {
		return this.firebaseInit.auth;
	}
	private readonly router: Router = inject(Router);
	private readonly menuController: MenuController = inject(MenuController);
	private readonly storageService: StorageService = inject(StorageService);
	private readonly location: Location = inject(Location);
	private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
	private readonly translateService: TranslateHelperService = inject(TranslateHelperService);

	/**
	 * Retourne true si l'utilisateur est connecté et a vérifié son email.
	 *
	 * @returns true si l'utilisateur est connecté et vérifié
	 */
	get isLoggedIn(): boolean {
		return this.isEmailVerified();
	}

	/**
	 * Vérifie si l'email de l'utilisateur actuel est vérifié.
	 *
	 * @returns true si l'email est vérifié
	 */
	isEmailVerified(): boolean {
		const currentUser = this.auth.currentUser;
		return currentUser?.emailVerified ?? false;
	}

	/**
	 * Connecte un utilisateur avec email et mot de passe.
	 *
	 * @param email - L'email de l'utilisateur
	 * @param password - Le mot de passe
	 * @param redirectYes - Route de redirection après connexion réussie
	 * @throws Erreur si la connexion échoue
	 */
	async login(email: string, password: string, redirectYes: string): Promise<void> {
		try {
			const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
			const user: User = userCredential.user;

			if (!user.emailVerified) {
				await this.router.navigateByUrl(this.translateService.localize('/verify-email'));
				return;
			}

			// Sauvegarder les informations de connexion
			this.storageService.set('isLoggin', 'true');
			this.storageService.set('uid', user.uid);
			await this.menuController.enable(true);

			// Récupérer le profil utilisateur
			const profileRef = doc(this.firestore, `profile/${user.uid}`);
			const profileDoc = await getDoc(profileRef);

			if (!profileDoc.exists()) {
				return;
			}

			const profileData = profileDoc.data();
			if (!profileData) {
				return;
			}

			// Vérifier si le profil est complet
			const username = profileData['username'];
			const isIncompleteProfile =
				!username ||
				username === 'SlaveousUser' ||
				username === '';

			this.storageService.set('completed', String(isIncompleteProfile));

			// Mettre à jour la date de dernière connexion
			await updateDoc(profileRef, {lastLoginAt: Date.now()});

			// Rediriger selon l'état du profil
			if (profileData['emailVerified']) {
				if (isIncompleteProfile) {
					await this.router.navigateByUrl(this.translateService.localize('/app/profile-set'));
				} else {
					await this.router.navigateByUrl(this.translateService.localize(redirectYes));
				}
			} else {
				await this.router.navigateByUrl(this.translateService.localize('/verify-email'));
			}
		} catch (error) {
			this.errorHandler.handleError(error);
			throw error;
		}
	}

	/**
	 * Déconnecte l'utilisateur actuel.
	 *
	 * @throws Erreur si la déconnexion échoue
	 */
	async logout(): Promise<void> {
		try {
			await signOut(this.auth);
			this.storageService.set('isLoggin', 'false');
			this.storageService.set('uid', null);
			await this.router.navigateByUrl(this.translateService.localize('/'));
		} catch (error) {
			this.errorHandler.handleError(error);
			throw error;
		}
	}

	/**
	 * Envoie un email de vérification à l'utilisateur actuel.
	 *
	 * @returns Promise résolue après l'envoi de l'email
	 * @throws Erreur si aucun utilisateur n'est connecté ou si l'envoi échoue
	 */
	async sendVerificationMail(): Promise<void> {
		const currentUser = this.auth.currentUser;

		if (!currentUser) {
			await this.router.navigateByUrl(this.translateService.localize('/'));
			return;
		}

		try {
			const verificationUrl = window.location.origin + this.location.prepareExternalUrl(this.translateService.localize('/app/profile-set'));
			await sendEmailVerification(currentUser, {url: verificationUrl});
			await this.router.navigateByUrl(this.translateService.localize('/verify-email'));
		} catch (error) {
			this.errorHandler.handleError(error);
			throw error;
		}
	}

	/**
	 * Envoie un email de réinitialisation de mot de passe.
	 *
	 * @param passwordResetEmail - L'email pour lequel réinitialiser le mot de passe
	 * @returns Promise résolue après l'envoi de l'email
	 * @throws Erreur si l'envoi échoue
	 */
	async passwordRecover(passwordResetEmail: string): Promise<void> {
		try {
			await sendPasswordResetEmail(this.auth, passwordResetEmail);
		} catch (error) {
			this.errorHandler.handleError(error);
			throw error;
		}
	}
}
